// Forgotten timers close themselves. A sleep, feed, bottle or pump timer that runs past any real length
// (core sanity.ts) gets a guessed end (core autoEndAt) and data.autoEnded, so it never blocks the log and never
// counts as an 88-hour feed. Every such entry waits in "Check these entries" (Track › Day) until a parent says
// it looks right or fixes the time; saving it in the log sheet clears the flag too.
// Runs on every tab (mounted in the app shell), on whichever family phone opens the app first.
import { useEffect, useMemo } from 'react';
import { closeForgotten, runningTooLong } from '@babyplan/core';
import { store } from './store/db';
import { updateEntry, type EntryPatch } from './store/entries';
import { useLiveQuery, useOpenEntries, useStoreOpen, type LocalEntry } from './store';

// own categories are left alone: an event-type category also has no end, and only the parent knows which is which
const AUTO = new Set(['sleep', 'breast', 'bottle', 'pump']);

// One try per entry per app start: if the server still refuses the fix, the entry must not bounce back and forth
// (a refused change is restored locally, which would trigger another try, another refusal… a request loop).
const tried = new Set<string>();

export function useAutoClose(babyId: string | undefined, tz: string | undefined) {
  const open = useOpenEntries(babyId);
  useEffect(() => {
    if (!babyId || !tz) return;
    const run = () => {
      const now = Date.now();
      for (const e of open) {
        if (!AUTO.has(e.type) || e.endAt !== null || e.deletedAt || tried.has(e.id)) continue;
        if (!runningTooLong(e.type, e.startAt, now)) continue;
        tried.add(e.id);
        // the end stays within the type's limit and late "resumed" sides are dropped (core closeForgotten)
        const fixed = closeForgotten({ type: e.type, startAt: e.startAt, data: e.data as Record<string, unknown> }, now, tz);
        const patch: EntryPatch = { endAt: fixed.endAt, data: fixed.data };
        void updateEntry(e.id, patch);
      }
    };
    run();
    const iv = window.setInterval(run, 5 * 60_000);
    return () => clearInterval(iv);
  }, [open, babyId, tz]);
}

/** Entries the app closed itself (data.autoEnded) in the last 90 days, newest first. */
export function useAutoEnded(babyId: string | undefined): LocalEntry[] {
  const ready = useStoreOpen();
  const rows = useLiveQuery<LocalEntry[]>(
    async () => {
      if (!ready || !babyId) return [];
      const lo = new Date(Date.now() - 90 * 86_400_000).toISOString();
      const all = await store().entries.where('[babyId+startAt]').between([babyId, lo], [babyId, '￿']).toArray();
      return all.filter((e) => !e.deletedAt && (e.data as { autoEnded?: unknown }).autoEnded);
    },
    [ready, babyId],
    [],
  );
  return useMemo(() => [...rows].sort((a, b) => b.startAt.localeCompare(a.startAt)), [rows]);
}

/** "Looks right": keep the guessed end, drop the flag. */
export async function confirmAutoEnded(e: LocalEntry) {
  const data = { ...(e.data as Record<string, unknown>) };
  delete data.autoEnded;
  await updateEntry(e.id, { data });
}
